import { X } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Button } from './Button'

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  open: boolean
  title: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])
  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button type="button" aria-label="Close" className="absolute inset-0" style={{ background: 'var(--surface-overlay)' }} onClick={onClose} />
      <div className={`relative flex min-h-0 max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden ${wide ? 'max-w-2xl' : 'max-w-md'} animate-fade-in rounded-lg border border-line bg-surface-raised shadow-token-lg`}>
        <div className="flex shrink-0 items-center justify-between border-b border-line-subtle px-4 py-3">
          <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
          <Button variant="ghost" className="!p-1" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-h-0 overflow-y-auto overscroll-contain px-4 py-4 text-[13px] text-ink-secondary">{children}</div>
        {footer && <div className="flex shrink-0 justify-end gap-2 border-t border-line-subtle px-4 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
